import { useEffect, useState, useCallback, memo, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosArrowDown } from "react-icons/io";
import { HiOutlineCheckCircle } from "react-icons/hi2";
import Question from "./Question";
import { useTranslation } from "react-i18next";
import { QuestionItem } from "types/assessmentTypes";

type CriteriaType = {
  id: number;
  title: string;
  questions: QuestionItem[];
  index: number;
  rowIndex: number;
};

type Props = {
  data: CriteriaType;
  answersMap: Map<number, number>;
  onAnswerChange: (questionId: number, optionId: number | null) => void;
  isFirst?: boolean;
  allCriteriaIds?: number[];
  currentCriteriaId?: number;
  onAutoOpenNext?: (currentId: number) => void;
  descriptionsMap: Map<number, string>;
  onDescriptionChange: (questionId: number, description: string) => void;
  type?: "company" | "organization";
};

const Accordion = memo(function Accordion({
  data,
  answersMap,
  onAnswerChange,
  isFirst = false,
  allCriteriaIds = [],
  currentCriteriaId = data.id,
  onAutoOpenNext,
  descriptionsMap,
  onDescriptionChange,
  type,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(isFirst);

  const { progress, isComplete } = useMemo(() => {
    const totalCount = data.questions.length;
    const answeredCount = data.questions.filter((q) =>
      answersMap.has(q.id)
    ).length;
    const progressPercent =
      totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;
    return {
      total: totalCount,
      answered: answeredCount,
      progress: progressPercent,
      isComplete: progressPercent === 100,
    };
  }, [data.questions, answersMap]);

  const isFullyAnswered = useMemo(() => {
    return data.questions.every((q) => answersMap.has(q.id));
  }, [data.questions, answersMap]);

  useEffect(() => {
    if (isFullyAnswered && isOpen) {
      const currentIndex = allCriteriaIds.indexOf(currentCriteriaId);
      const nextCriteriaId = allCriteriaIds[currentIndex + 1];

      if (nextCriteriaId && onAutoOpenNext) {
        const timer = setTimeout(() => {
          onAutoOpenNext(nextCriteriaId);
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [
    isFullyAnswered,
    isOpen,
    allCriteriaIds,
    currentCriteriaId,
    onAutoOpenNext,
  ]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleAnswerChange = useCallback(
    (questionId: number, optionId: number | null) => {
      onAnswerChange(questionId, optionId);
    },
    [onAnswerChange]
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="group overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm transition-all duration-200 hover:shadow-md dark:border-gray-700/50 dark:bg-navy-800/90"
    >
      <button
        onClick={handleToggle}
        className="relative flex w-full items-center justify-between px-5 py-4 transition-colors hover:bg-gray-50 dark:hover:bg-navy-700/50"
      >
        <div className="flex items-center gap-3">
          {/* Progress Circle */}
          <div className="relative h-9 w-9 flex-shrink-0">
            <svg className="h-9 w-9 -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeDasharray="100"
                strokeDashoffset={100 - progress}
                className="text-brand-500 transition-all duration-300 ease-out"
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-gray-600 dark:text-gray-300">
              {progress}%
            </span>
          </div>

          <span className="text-md font-extrabold text-brand-800 dark:text-white">
            {data.rowIndex} -{" "}
            {type === "company" ? t(`criteria.${data.index}`) : data.title}
          </span>

          {isComplete && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, duration: 0.2 }}
            >
              <HiOutlineCheckCircle className="h-5 w-5 text-green-500" />
            </motion.div>
          )}
        </div>

        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <IoIosArrowDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </button>

      {/* Progress Bar */}
      <div className="h-0.5 w-full bg-gray-100 dark:bg-gray-800">
        <motion.div
          className="h-0.5 bg-gradient-to-r from-brand-500 to-brand-600"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 p-5">
              {data.questions.map((q, index) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.2) }}
                >
                  <Question
                    question={q}
                    index={index}
                    selectedOption={answersMap.get(q.id)}
                    onOptionSelect={handleAnswerChange}
                    description={descriptionsMap.get(q.id) || ""}
                    onDescriptionChange={onDescriptionChange}
                    type={type}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

export default Accordion;
